import { useCallback, useEffect, useRef, useState } from 'react';
import type { SegmentHandle } from '../../../shared/lib/schedule';
import {
  DRAFT_DEFAULTS,
  applyDraftDrag,
  pickFromOffsetPx,
  type PlanningDraftState,
  type PlanningSeed,
} from './draft';
import type { DispositionModeName } from './useDispositionMode';

/**
 * The one owner of the placed-but-not-saved draft.
 *
 * The draft is transient pointer state, so it lives in `useState` — nuqs is for filters, modals and
 * pagination, and everything needed to reconstruct planning from a pasted link is already in the
 * mode params; the slot is re-picked.
 *
 * `seed` is the one exception to "re-picked": a reschedule arrives already knowing where the thing
 * is, so the draft starts on that slot instead of waiting for a click.
 */
export function usePlanningDraft(
  dayIso: string,
  mode: DispositionModeName | null,
  seed?: PlanningSeed | null,
) {
  const [draft, setDraft] = useState<PlanningDraftState | null>(null);

  /**
   * Which seed the draft below came from, `key:dayIso`.
   *
   * The seed is rebuilt from a React Query result (`useRescheduleSource` reads the appointment or
   * task detail), so its **object identity changes on every refetch** — a window focus is enough,
   * and every save invalidates it. Re-applying on identity therefore snapped a draft the user had
   * dragged back onto the entry's stored slot, at a moment nothing on screen explained. What makes
   * a seed new is what it *says*, not which object says it.
   */
  const appliedSeedRef = useRef<string | null>(null);

  // A slot is a time on one specific day. Carrying it to another one would place work where nobody
  // looked, so the draft dies with the day and with the mode.
  useEffect(() => {
    setDraft(null);
    // Cleared with the draft, so leaving the day and coming back re-seeds — the planning bar's
    // "back to the current slot" jump is exactly that round trip.
    appliedSeedRef.current = null;
  }, [dayIso, mode]);

  // Declared after the clear above so it wins on mount. Nothing a drag does changes these deps.
  useEffect(() => {
    if (!mode || !seed || seed.dayIso !== dayIso) return;

    const applied = `${seed.key}:${dayIso}`;
    if (appliedSeedRef.current === applied) return;

    appliedSeedRef.current = applied;
    setDraft(seed.state);
  }, [seed, dayIso, mode]);

  /**
   * A click on a row track. A seeded draft lends its durations even on another day — the Termin
   * being moved keeps its size wherever it lands; only a fresh placement uses the mode's defaults.
   */
  const place = useCallback(
    (technicianId: number, offsetPx: number) => {
      if (!mode) return;
      setDraft(pickFromOffsetPx(offsetPx, technicianId, seed?.state.durations ?? DRAFT_DEFAULTS[mode]));
    },
    [mode, seed],
  );

  /** A finished drag gesture on the draft — pure state update, nothing is sent anywhere. */
  const applyDrag = useCallback(
    (handle: 'move' | SegmentHandle, deltaMinutes: number, technicianId: number) => {
      setDraft((current) =>
        current ? applyDraftDrag(current, handle, deltaMinutes, technicianId) : current,
      );
    },
    [],
  );

  const clear = useCallback(() => setDraft(null), []);

  return { draft, place, applyDrag, clear };
}
